import { useState, useEffect } from 'react';
import { getClients, createClient } from '@/lib/api/clients';

interface Client {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  company?: string;
}

interface ClientSelectorProps {
  value?: number | null;
  onChange: (clientId: number) => void;
  error?: string;
}

export const ClientSelector = ({ value, onChange, error }: ClientSelectorProps) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  
  // New client form state
  const [newClient, setNewClient] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    address: '',
  });
  const [saving, setSaving] = useState(false);
  const [addError, setAddError] = useState('');

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    setLoading(true);
    try {
      const clients = await getClients(searchTerm);
      setClients((clients || []) as unknown as Client[]);
    } catch (error) {
      console.error('Failed to load clients:', error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      loadClients();
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const selectedClient = clients.find(c => c.id === value);

  const handleSelectClient = (clientId: number) => {
    onChange(clientId);
    setShowDropdown(false);
  };

  const handleAddClient = async () => {
    if (!newClient.name.trim()) return;
    setAddError('');
    setSaving(true);
    try {
      const created = await createClient(newClient);
      await loadClients();
      onChange(parseInt(created.id));
      setShowAddModal(false);
      setNewClient({ name: '', email: '', phone: '', company: '', address: '' });
    } catch (error) {
      console.error('Failed to create client:', error);
      setAddError('Failed to create client. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.phone?.includes(searchTerm)
  );

  return (
    <div className="relative">
      <label className="block text-sm font-medium text-gray-900 mb-1">
        Client *
      </label>
      
      <div className="relative">
        <input
          type="text"
          value={selectedClient ? selectedClient.name : searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setShowDropdown(true);
            if (selectedClient) {
              onChange(0);
            }
          }}
          onFocus={() => setShowDropdown(true)}
          onBlur={(e) => {
            // Only close dropdown if clicking outside the dropdown
            const currentTarget = e.currentTarget;
            setTimeout(() => {
              if (!currentTarget.parentElement?.parentElement?.contains(document.activeElement)) {
                setShowDropdown(false);
              }
            }, 200);
          }}
          placeholder="Search for a client..."
          className={`w-full px-4 py-2 rounded-lg border ${
            error
              ? 'border-error focus:border-error'
              : 'border-gray-200 focus:border-primary'
          } bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20`}
        />
        <button
          type="button"
          onClick={() => setShowDropdown(!showDropdown)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2"
        >
          <span className="material-symbols-outlined text-gray-600">
            {showDropdown ? 'expand_less' : 'expand_more'}
          </span>
        </button>
      </div>

      {error && (
        <p className="mt-1 text-sm text-error">{error}</p>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <div 
          className="absolute z-50 mt-2 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto"
          onMouseDown={(e) => e.preventDefault()}
        >
          {loading ? (
            <div className="p-4 text-center text-gray-600">
              Loading clients...
            </div>
          ) : filteredClients.length > 0 ? (
            <>
              {filteredClients.map((client) => (
                <button
                  key={client.id}
                  type="button"
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSelectClient(client.id);
                  }}
                  className="w-full text-left px-4 py-3 hover:bg-primary/10 transition-colors border-b border-gray-200 last:border-0"
                >
                  <div className="font-medium text-gray-900">
                    {client.name}
                  </div>
                  <div className="text-sm text-gray-600 flex items-center gap-4">
                    {client.email && (
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">email</span>
                        {client.email}
                      </span>
                    )}
                    {client.phone && (
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">phone</span>
                        {client.phone}
                      </span>
                    )}
                  </div>
                </button>
              ))}
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowAddModal(true);
                  setShowDropdown(false);
                }}
                className="w-full text-left px-4 py-3 bg-primary/5 hover:bg-primary/10 transition-colors flex items-center gap-2 text-primary font-medium"
              >
                <span className="material-symbols-outlined">add_circle</span>
                Add New Client
              </button>
            </>
          ) : (
            <div className="p-4">
              <p className="text-gray-600 text-center mb-3">
                No clients found
              </p>
              <button
                type="button"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setShowAddModal(true);
                  setShowDropdown(false);
                }}
                className="w-full px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium flex items-center justify-center gap-2"
              >
                <span className="material-symbols-outlined">add_circle</span>
                Add New Client
              </button>
            </div>
          )}
        </div>
      )}

      {/* Add Client Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-gray-900">
                Add New Client
              </h2>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-600 hover:text-gray-900"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>

            <div className="space-y-4">
              {addError && (
                <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">{addError}</div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Client Name *
                </label>
                <input
                  type="text"
                  value={newClient.name}
                  onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && newClient.name.trim()) {
                      e.preventDefault();
                      handleAddClient();
                    }
                  }}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="John Doe"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Company
                </label>
                <input
                  type="text"
                  value={newClient.company}
                  onChange={(e) => setNewClient({ ...newClient, company: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="ABC Corp"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={newClient.email}
                  onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="john@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={newClient.phone}
                  onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-1">
                  Address
                </label>
                <textarea
                  value={newClient.address}
                  onChange={(e) => setNewClient({ ...newClient, address: e.target.value })}
                  rows={3}
                  className="w-full px-4 py-2 rounded-lg border border-gray-200 bg-gray-50 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
                  placeholder="123 Main St, City, State 12345"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  disabled={saving}
                  className="flex-1 px-4 py-2 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleAddClient}
                  disabled={saving || !newClient.name.trim()}
                  className="flex-1 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? 'Adding...' : 'Add Client'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

